import { Button, Flex, Group, Modal, Stack, Text, Textarea } from "@mantine/core";
import clsx from "clsx";
import { type JSX } from "react";

import type { SummaryResolution } from "@/shared-types";

import { withPolymorphicClickAnalytics } from "@/analytics";
import { DynamicCallbackForm } from "@/call-controls/ScheduleCallback/components/CallbackForm/DynamicCallbackForm";
import { RadioCard, Warning } from "@/common-ui";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";

import type { SessionSummaryViewProps } from "./sessionSummaryView";

import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { getPartnerTool } from "../utils";
import { CustomerDroppedNotification } from "./CustomerDroppedNotification";
import styles from "./DynamicSessionSummary.module.css";
import { SendSummaryNotification } from "./SendSummaryNotification";
import { CALLBACK_CUTOFF, MAX_SUMMARY_LENGTH } from "./utils";

const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "Click");

const CALL_RESULTS = [
    {
        id: "resolved-radio-button",
        value: "resolved",
        label: "Issue resolved",
        testId: "session-summary-problem-solved-yes",
        actionId: "SessionSummaryModal_ProblemSolvedYes",
    },
    {
        id: "not-resolved-radio-button",
        value: "not resolved",
        label: "Issue unresolved",
        testId: "session-summary-problem-solved-no",
        actionId: "SessionSummaryModal_ProblemSolvedNo",
    },
    {
        id: "transfer-radio-button",
        value: "transferred",
        label: "Transferred to another agent/team",
        testId: "session-summary-problem-solved-transferred",
        actionId: "SessionSummaryModal_ProblemSolvedTransferred",
    },
] as const satisfies readonly {
    id: string;
    value: Exclude<SummaryResolution, null>;
    label: string;
    testId: string;
    actionId: string;
}[];

const GENERIC_END_LABEL = "End session";

function submitButtonLabel(buttonText: string): string {
    return buttonText === GENERIC_END_LABEL ? "Submit and end session" : buttonText;
}

function notesDescription(shouldCopy: boolean, shouldDisableTextArea: boolean, partnerTool: string): string {
    if (!shouldCopy) return "Describe the issue";
    if (shouldDisableTextArea) return `Copy these notes into ${partnerTool}.`;
    return `Add the notes to copy into ${partnerTool}.`;
}

/** Prevent mantine's attempt to close the modal when the outside overlay is clicked */
const interceptClose = () => null;

function formatCountdown(totalSeconds: number): string {
    const safeSeconds = Math.max(0, totalSeconds);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")} sec`;
}

function ResultOption({
    id,
    label,
    value,
    testId,
    actionId,
    selected,
}: {
    id: string;
    label: string;
    value: Exclude<SummaryResolution, null>;
    testId: string;
    actionId: string;
    selected: boolean;
}) {
    return (
        <RadioCard
            id={id}
            fullWidth
            color="var(--mantine-color-dark-5)"
            radioColor="asurion.4"
            label={label}
            selected={selected}
            data-testid={testId}
            analytics={{ actionId }}
            onClick={() => useSessionSummaryStore.getState().setResolved(value)}
        />
    );
}

type DynamicSummaryFormProps = Pick<
    SessionSummaryViewProps,
    | "partner"
    | "sessionSummaryLoading"
    | "shouldDisableTextArea"
    | "shouldShowTextArea"
    | "canSendSuccessfulSummary"
    | "buttonText"
    | "shouldCopy"
    | "summaryUnavailable"
    | "canCallback"
    | "callbacksRemaining"
    | "setView"
    | "onCopyOrEndSession"
> & {
    reachedCallbackCutoff: boolean;
};

function FieldLabel({ children, required, htmlFor }: { children: string; required?: boolean; htmlFor?: string }) {
    return (
        <Text component="label" htmlFor={htmlFor}>
            {children}
            {required ? <Text component="span">*</Text> : null}
        </Text>
    );
}

function DynamicSummaryForm({
    partner,
    sessionSummaryLoading,
    shouldDisableTextArea,
    shouldShowTextArea,
    canSendSuccessfulSummary,
    buttonText,
    shouldCopy,
    summaryUnavailable,
    canCallback,
    callbacksRemaining,
    setView,
    onCopyOrEndSession,
    reachedCallbackCutoff,
}: DynamicSummaryFormProps) {
    const { callbackEnabled } = usePartnerFeatureConfigured();
    const summary = useSessionSummaryStore((state) => state.summary);
    const resolutionStatus = useSessionSummaryStore((state) => state.resolutionStatus);
    const partnerTool = getPartnerTool(partner);
    const notesMissing = summary.trim().length === 0;
    const submitDisabled = summaryUnavailable || notesMissing || resolutionStatus === null || sessionSummaryLoading;
    const showCallbackPrompt = callbackEnabled && canCallback && callbacksRemaining > 0 && !reachedCallbackCutoff;

    return (
        <Stack gap="lg">
            <Stack gap="3xs">
                {shouldShowTextArea ? (
                    <>
                        <FieldLabel required htmlFor="dynamic-session-summary-notes">
                            {`${partnerTool} notes`}
                        </FieldLabel>
                        <Textarea
                            id="dynamic-session-summary-notes"
                            className="fs-mask"
                            classNames={{
                                input: clsx(styles.notesInput),
                                description: styles.description,
                            }}
                            variant="filled"
                            description={notesDescription(shouldCopy, shouldDisableTextArea, partnerTool)}
                            maxLength={MAX_SUMMARY_LENGTH}
                            rows={3}
                            disabled={shouldDisableTextArea || sessionSummaryLoading}
                            value={summary}
                            onChange={(event) =>
                                useSessionSummaryStore
                                    .getState()
                                    .setSessionSummary({ value: event.currentTarget.value })
                            }
                            data-testid="session-summary-text-input"
                        />
                    </>
                ) : (
                    <SendSummaryNotification
                        partner={partner}
                        loading={sessionSummaryLoading}
                        summarySendSucceeded={canSendSuccessfulSummary}
                    />
                )}
            </Stack>

            <Stack gap="xs">
                <Text>What was the result of this call?</Text>
                <Stack className={styles.optionList} gap="xs">
                    {CALL_RESULTS.map((result) => (
                        <ResultOption
                            key={result.value}
                            id={result.id}
                            label={result.label}
                            value={result.value}
                            testId={result.testId}
                            actionId={result.actionId}
                            selected={resolutionStatus === result.value}
                        />
                    ))}
                </Stack>
            </Stack>

            <AnalyticsButton
                analytics={{ actionId: "SessionSummaryModal_SendButton" }}
                className={styles.primaryButton}
                fullWidth
                disabled={submitDisabled}
                onClick={() => void onCopyOrEndSession()}
                data-testid="call-controls-end-session"
            >
                {submitButtonLabel(buttonText)}
            </AnalyticsButton>

            {showCallbackPrompt ? (
                <Stack className={styles.callbackPrompt} gap="xs">
                    <Text className={styles.promptTitle}>Call dropped or ended early?</Text>
                    <Text className={clsx(styles.hint, styles.centeredHint)}>
                        Only use this if the call ended unexpectedly and you still need to reach the customer.
                    </Text>
                    <AnalyticsButton
                        analytics={{ actionId: "SessionSummaryModal_CallCustomerButton" }}
                        className={styles.callBackButton}
                        fullWidth
                        variant="outline"
                        mt="xs"
                        onClick={() => setView("schedule-callback")}
                        data-testid="dynamic-session-summary-call-back"
                    >
                        Call customer back
                    </AnalyticsButton>
                </Stack>
            ) : null}
        </Stack>
    );
}

/**
 * Callback-oriented wrap-up. The summary collects notes and a call result; "Call customer back"
 * switches to scheduling a callback against the same interaction.
 */
export function DynamicSessionSummary({
    opened,
    showCallEndedBanner,
    view,
    summaryHeader,
    canCallback,
    partner,
    sessionSummaryLoading,
    shouldDisableTextArea,
    shouldShowTextArea,
    canSendSuccessfulSummary,
    buttonText,
    summaryUnavailable,
    callbacksRemaining,
    shouldCopy,
    expirySeconds,
    setView,
    onCopyOrEndSession,
    onScheduleCallback,
}: SessionSummaryViewProps): JSX.Element {
    const { callbackEnabled } = usePartnerFeatureConfigured();
    const showLimitWarning = callbackEnabled && !canCallback;

    const reachedCallbackCutoff = expirySeconds <= CALLBACK_CUTOFF;
    return (
        <Group className={styles.modalContainer}>
            <Modal
                centered
                transitionProps={{ transition: "pop" }}
                classNames={{
                    inner: styles.modalInner,
                    overlay: styles.modalOverlay,
                    content: styles.modalContent,
                    header: showCallEndedBanner ? styles.modalHeader : styles.modalHeaderHidden,
                    body: styles.modalBody,
                }}
                onClose={interceptClose}
                opened={opened}
                overlayProps={{ backgroundOpacity: 0.1 }}
                size="26rem"
                withCloseButton={false}
                title={showCallEndedBanner ? <CustomerDroppedNotification /> : null}
            >
                <Flex className={styles.header} align="center" justify="space-between">
                    <Stack gap={0}>
                        <Text className={styles.title} component="h2">
                            {view === "schedule-callback" ? "Schedule a callback" : summaryHeader}
                        </Text>
                        {showLimitWarning && view === "edit-summary" ? (
                            <Text className={styles.limitWarning}>You’ve reached the callback limit.</Text>
                        ) : null}
                    </Stack>
                    <Text className={styles.timerBadge}>{formatCountdown(expirySeconds)}</Text>
                </Flex>
                {view === "schedule-callback" ? (
                    <>
                        <DynamicCallbackForm
                            callbackSurface="wrapUp"
                            afterSubmitClickDefault={() => void onScheduleCallback()}
                            reachedCallbackCutoff={reachedCallbackCutoff}
                        />
                        {reachedCallbackCutoff && (
                            <Group pt="3xs" gap={5} justify="center">
                                <Warning width="10" fill="var(--mantine-color-red-3)" />
                                <Text pl={0} fz="xs" c="red.3">
                                    Sorry, there’s not enough time to schedule a callback
                                </Text>
                            </Group>
                        )}
                    </>
                ) : (
                    <DynamicSummaryForm
                        partner={partner}
                        sessionSummaryLoading={sessionSummaryLoading}
                        shouldDisableTextArea={shouldDisableTextArea}
                        shouldShowTextArea={shouldShowTextArea}
                        canSendSuccessfulSummary={canSendSuccessfulSummary}
                        buttonText={buttonText}
                        shouldCopy={shouldCopy}
                        summaryUnavailable={summaryUnavailable}
                        canCallback={canCallback}
                        callbacksRemaining={callbacksRemaining}
                        setView={setView}
                        onCopyOrEndSession={onCopyOrEndSession}
                        reachedCallbackCutoff={reachedCallbackCutoff}
                    />
                )}
            </Modal>
        </Group>
    );
}
